import * as THREE from 'three';
import { isOrthographicCamera } from './isOrthographicCamera';

const bbox = /* @__PURE__ */ new THREE.Box3();
const veca = /* @__PURE__ */ new THREE.Vector3();
const vecb = /* @__PURE__ */ new THREE.Vector3();

export interface FitArgs {
  /** New camera position */
  position: THREE.Vector3;
  /** Look-at target */
  target: THREE.Vector3;
  /** (Optional) up vector, defaults to camera.up */
  up?: THREE.Vector3;
}

/**
 * Compute the camera position & target to "fit" either:
 *  - a THREE.Object3D (including its children)
 *  - or an explicit THREE.Box3 (in world space)
 */
export function calculateFitArgs(
  subject: THREE.Object3D | THREE.Box3,
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  margin = 1.2,
): FitArgs {
  const box = subject instanceof THREE.Box3 ? bbox.copy(subject) : bbox.setFromObject(subject);

  if (box.isEmpty()) {
    const fallback = camera.position.length() || 10;
    box.setFromCenterAndSize(veca.setScalar(0), vecb.setScalar(fallback));
  }

  const size = box.getSize(veca);
  const center = box.getCenter(vecb);
  const maxDim = Math.max(size.x, size.y, size.z);

  let distance: number;
  if (isOrthographicCamera(camera)) {
    // orthographic: pick a zoom level
    // (drei uses *4 arbitrarily, feel free to tweak)
    distance = maxDim * 4 * margin;
  } else {
    // perspective: derive distance from fov/aspect so that the largest
    // dimension spans the frustum.
    const fovRad = (camera.fov * Math.PI) / 180;
    const hDist = maxDim / (2 * Math.tan(fovRad / 2));
    const wDist = hDist / camera.aspect;
    distance = margin * Math.max(hDist, wDist);
  }

  const direction = camera.position.clone().sub(center).normalize();

  const newPos = center.clone().addScaledVector(direction, distance);

  newPos.y = Math.max(size.length(), size.y) * 1.5;

  return {
    position: newPos,
    target: center,
    up: camera.up.clone(),
  };
}
